import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const SERIES_COLORS: Record<string, string> = {
  inlet_m3: '#2563eb',
  outlet_m3: '#22c55e',
  value: 'rgb(var(--color-app-accent))',
};

const DEFAULT_COLORS = ['#2563eb', '#22c55e', '#f59e0b', '#8b5cf6'];

export interface InlineChatChartSpec {
  type: string;
  title?: string;
  x_key: string;
  y_key?: string;
  y_keys?: string[];
  series_labels?: Record<string, string>;
  unit?: string;
  data: Record<string, unknown>[];
}

interface InlineChatLineChartProps {
  spec: InlineChatChartSpec;
}

function resolveSeriesKeys(spec: InlineChatChartSpec): string[] {
  if (Array.isArray(spec.y_keys) && spec.y_keys.length > 0) {
    return spec.y_keys;
  }
  if (typeof spec.y_key === 'string' && spec.y_key.length > 0) {
    return [spec.y_key];
  }
  return [];
}

function seriesLabel(spec: InlineChatChartSpec, key: string): string {
  return spec.series_labels?.[key] ?? key;
}

function seriesColor(key: string, index: number): string {
  return SERIES_COLORS[key] ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length];
}

function isValidChartSpec(spec: InlineChatChartSpec): boolean {
  const seriesKeys = resolveSeriesKeys(spec);
  if (
    spec?.type !== 'line' ||
    typeof spec.x_key !== 'string' ||
    seriesKeys.length === 0 ||
    !Array.isArray(spec.data) ||
    spec.data.length === 0
  ) {
    return false;
  }

  return spec.data.every(
    (point) =>
      point &&
      typeof point === 'object' &&
      spec.x_key in point &&
      seriesKeys.every((key) => key in point),
  );
}

export default function InlineChatLineChart({ spec }: InlineChatLineChartProps) {
  const seriesKeys = resolveSeriesKeys(spec);

  if (!isValidChartSpec(spec)) {
    return (
      <div className="my-3 rounded-lg border border-app-border bg-app-muted/30 px-3 py-6 text-center text-sm text-app-text-dim">
        ไม่มีข้อมูลสำหรับกราฟ
      </div>
    );
  }

  return (
    <div className="my-3 w-full min-w-0 rounded-xl border border-app-border bg-app-surface p-3 shadow-sm">
      {spec.title ? (
        <h4 className="mb-2 text-sm font-semibold text-app-text">{spec.title}</h4>
      ) : null}
      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={spec.data}
            margin={{ top: 8, right: 12, bottom: 8, left: 0 }}
          >
            <CartesianGrid
              stroke="rgb(var(--color-app-border))"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey={spec.x_key}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
              tick={{
                fill: 'rgb(var(--color-app-text-dim))',
                fontSize: 10,
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fill: 'rgb(var(--color-app-text-dim))',
                fontSize: 10,
              }}
              label={
                spec.unit
                  ? {
                      value: spec.unit,
                      angle: -90,
                      position: 'insideLeft',
                      fill: 'rgb(var(--color-app-text-dim))',
                      fontSize: 10,
                    }
                  : undefined
              }
            />
            <Tooltip
              contentStyle={{
                background: 'rgb(var(--color-app-surface))',
                border: '1px solid rgb(var(--color-app-border))',
                borderRadius: 8,
                color: 'rgb(var(--color-app-text))',
                fontSize: 12,
              }}
              labelStyle={{ color: 'rgb(var(--color-app-text))' }}
              formatter={(value, name) => [
                `${String(value)}${spec.unit ? ` ${spec.unit}` : ''}`,
                seriesLabel(spec, String(name)),
              ]}
            />
            {seriesKeys.length > 1 ? (
              <Legend
                formatter={(value) => seriesLabel(spec, String(value))}
                wrapperStyle={{ fontSize: 12 }}
              />
            ) : null}
            {seriesKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={key}
                stroke={seriesColor(key, index)}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
